import './App.css';
import { Toaster } from '@/components/ui/toaster';
import { pagesConfig } from './pages.config';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import Login from './pages/Login';
import StaffLogin from './pages/StaffLogin';
import StudentLogin from './pages/StudentLogin';
import ChangePassword from './pages/ChangePassword';
import LoginPreview from './pages/LoginPreview';
import AuthCallback from './pages/AuthCallback';
import AccessDenied from './pages/AccessDenied';
import Kiosk from './pages/Kiosk';

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : () => null;

const LayoutWrapper = ({ children, currentPageName }) =>
  Layout ? <Layout currentPageName={currentPageName}>{children}</Layout> : <>{children}</>;

const LoadingScreen = () => (
  <div className="fixed inset-0 flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
  </div>
);

// Protected wrapper - redirects to /login when not authenticated
const RequireAuth = ({ children }) => {
  const { user, isLoadingAuth } = useAuth();
  if (isLoadingAuth) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AppRoutes = () => {
  const location = useLocation();

  // Detect OAuth callback synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/staff-login" element={<StaffLogin />} />
      <Route path="/student-login" element={<StudentLogin />} />
      <Route path="/change-password" element={<ChangePassword />} />
      <Route path="/login-preview" element={<LoginPreview />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route path="/kiosk" element={<Kiosk />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <LayoutWrapper currentPageName={mainPageKey}>
              <Routes>
                <Route path="/" element={<MainPage />} />
                {Object.entries(Pages).map(([path, Page]) => (
                  <Route key={path} path={`/${path.toLowerCase()}`} element={<Page />} />
                ))}
                <Route path="*" element={<PageNotFound />} />
              </Routes>
            </LayoutWrapper>
          </RequireAuth>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
      <Toaster />
    </AuthProvider>
  );
}

export default App;
